import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import "@fortawesome/fontawesome-free/css/all.min.css";
import PopupService from "../../../../services/PopupService";
import sanitizeHtml from "../../../../utils/sanitizeHtml";
import "./HomePopup.css";

const MOBILE_MEDIA_QUERY = "(max-width: 767px)";
const CSS_POSITION_PATTERN = /^(?:0|\d+(?:\.\d+)?(?:px|rem|em|%|vh|vw))$/;

const getPositionValue = (value, fallback) => {
  const normalizedValue = String(value || "").trim();
  return CSS_POSITION_PATTERN.test(normalizedValue)
    ? normalizedValue
    : fallback;
};

const getStorageKey = (popupId, stateName) =>
  `utrains:home-popup:${popupId}:${stateName}`;

const readSessionState = (popupId, stateName) => {
  try {
    return (
      window.sessionStorage.getItem(getStorageKey(popupId, stateName)) ===
      "true"
    );
  } catch {
    return false;
  }
};

const writeSessionState = (popupId, stateName, value) => {
  try {
    window.sessionStorage.setItem(
      getStorageKey(popupId, stateName),
      String(value)
    );
  } catch {
    // The popup still works when storage is unavailable.
  }
};

const getPageScrollPercent = () => {
  const documentElement = document.documentElement;
  const scrollableHeight = Math.max(
    documentElement.scrollHeight - window.innerHeight,
    0
  );

  if (scrollableHeight === 0) return 100;

  return (window.scrollY / scrollableHeight) * 100;
};

const normalizeScrollThreshold = (value) => {
  const threshold = Number(value);
  if (!Number.isFinite(threshold)) return 0;
  return Math.min(100, Math.max(0, threshold));
};

function HomePopup() {
  const [popup, setPopup] = useState(null);
  const [isDisplayReady, setIsDisplayReady] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    window.matchMedia(MOBILE_MEDIA_QUERY).matches
  );
  const popupRef = useRef(null);
  const closeButtonRef = useRef(null);
  const safeHeaderHtml = useMemo(
    () => sanitizeHtml(popup?.richTextHeader),
    [popup?.richTextHeader]
  );
  const safeContentHtml = useMemo(
    () => sanitizeHtml(popup?.richTextContent),
    [popup?.richTextContent]
  );

  const toggleMinimized = useCallback(() => {
    if (isMobile || !popup) return;

    setIsMinimized((current) => {
      const nextValue = !current;
      writeSessionState(popup.id, "minimized", nextValue);
      return nextValue;
    });
  }, [isMobile, popup]);

  const closePopup = useCallback(() => {
    if (!popup) return;

    writeSessionState(popup.id, "closed", true);
    setIsClosed(true);
  }, [popup]);

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    const handleViewportChange = (event) => setIsMobile(event.matches);

    setIsMobile(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleViewportChange);
    } else {
      mediaQuery.addListener(handleViewportChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleViewportChange);
      } else {
        mediaQuery.removeListener(handleViewportChange);
      }
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const getActivePopup = async () => {
      const response = await PopupService.getActive();

      if (!isMounted) return;

      if (
        response.status === "success" &&
        response.code !== 204 &&
        response.data
      ) {
        const activePopup = response.data;
        setPopup(activePopup);
        setIsClosed(readSessionState(activePopup.id, "closed"));
        setIsMinimized(readSessionState(activePopup.id, "minimized"));
      } else {
        setPopup(null);
        setIsDisplayReady(false);
      }
    };

    getActivePopup();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!popup || isClosed) {
      setIsDisplayReady(false);
      return undefined;
    }

    const threshold = normalizeScrollThreshold(
      isMobile ? popup.scrollPercentOnMobile : popup.scrollPercent
    );
    setIsDisplayReady(false);

    const checkScrollPosition = () => {
      if (threshold === 0 || getPageScrollPercent() >= threshold) {
        setIsDisplayReady(true);
        window.removeEventListener("scroll", checkScrollPosition);
      }
    };

    checkScrollPosition();

    if (threshold > 0 && getPageScrollPercent() < threshold) {
      window.addEventListener("scroll", checkScrollPosition, {
        passive: true,
      });
    }

    return () => {
      window.removeEventListener("scroll", checkScrollPosition);
    };
  }, [popup, isClosed, isMobile]);

  useEffect(() => {
    if (!isMobile || !popup || isClosed || !isDisplayReady) return undefined;

    const appRoot = document.getElementById("root");
    const previousActiveElement = document.activeElement;
    const previousBodyOverflow = document.body.style.overflow;
    const previousDocumentOverflow = document.documentElement.style.overflow;
    const previousRootInert = appRoot?.inert;
    const previousRootAriaHidden = appRoot?.getAttribute("aria-hidden");
    const focusFrame = window.requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
    });

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    if (appRoot) {
      appRoot.inert = true;
      appRoot.setAttribute("aria-hidden", "true");
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closePopup();
        return;
      }

      if (event.key !== "Tab" || !popupRef.current) return;

      const focusableElements = Array.from(
        popupRef.current.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => !element.hasAttribute("hidden"));

      if (focusableElements.length === 0) {
        event.preventDefault();
        popupRef.current.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousDocumentOverflow;

      if (appRoot) {
        appRoot.inert = Boolean(previousRootInert);
        if (previousRootAriaHidden === null) {
          appRoot.removeAttribute("aria-hidden");
        } else {
          appRoot.setAttribute("aria-hidden", previousRootAriaHidden);
        }
      }

      if (previousActiveElement instanceof HTMLElement) {
        previousActiveElement.focus();
      }
    };
  }, [closePopup, isClosed, isDisplayReady, isMobile, popup]);

  if (!popup || isClosed || !isDisplayReady) return null;

  const isEffectivelyMinimized = !isMobile && isMinimized;
  const popupPosition = isMobile
    ? undefined
    : {
        bottom: getPositionValue(popup.bottom, "24px"),
        left: getPositionValue(popup.leftPos, "24px"),
      };

  const popupElement = (
    <aside
      ref={popupRef}
      className={`home-popup ${
        isEffectivelyMinimized ? "is-minimized" : ""
      } ${isMobile ? "is-mobile" : ""}`}
      style={popupPosition}
      aria-label="Utrains information"
      aria-modal={isMobile ? "true" : undefined}
      role={isMobile ? "dialog" : undefined}
      tabIndex={isMobile ? -1 : undefined}
    >
      <header className="home-popup__header">
        <div
          className="home-popup__rich-header"
          dangerouslySetInnerHTML={{ __html: safeHeaderHtml }}
        ></div>

        <div className="home-popup__controls">
          {!isMobile ? (
            <button
              type="button"
              onClick={toggleMinimized}
              aria-label={isMinimized ? "Expand popup" : "Minimize popup"}
              title={isMinimized ? "Expand" : "Minimize"}
            >
              <i
                className={`fa-solid ${
                  isMinimized ? "fa-chevron-up" : "fa-minus"
                }`}
              ></i>
            </button>
          ) : null}
          <button
            ref={closeButtonRef}
            type="button"
            onClick={closePopup}
            aria-label="Close popup"
            title="Close"
          >
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>
      </header>

      {!isEffectivelyMinimized ? (
        <div
          className="home-popup__rich-content"
          dangerouslySetInnerHTML={{ __html: safeContentHtml }}
        ></div>
      ) : null}
    </aside>
  );

  return createPortal(
    isMobile ? (
      <div className="home-popup-overlay">{popupElement}</div>
    ) : (
      popupElement
    ),
    document.body
  );
}

export default HomePopup;
